import { Avatar, Tooltip } from "@mui/material";
import ActionDownload from "assets/img/svg/actionDownload";
import PickerCompany from "components/BPPickerCompany";
import BPSignedDocumentViewer from "components/shared/BPSignedDocumentViewer";
import ControlFilter from "components/shared/ControlFilter";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import SmallSearchBar from "components/shared/SmallSearchBar";
import { formatLocaleDateString, getBPAvatarUrl, getBPCompanyName } from "helpers/utils";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { SignedDocument } from "store/articleStore";
import { downloadSignedDocument, fetchAllSignedDocuments, fetchSignedDocumentContent } from "store/articleStore";
import type { RootState } from "store/store";
import { useAppDispatch } from "store/store";
import type { IBeneficiariesCompanies } from "store/types";

import { getCategory, searchHaystack, typeLabel } from "./utils";

type TProps = {
    beneficiarCompagnies: IBeneficiariesCompanies[];
    headerText?: string;
};

const BPSignedDocumentsList = ({ beneficiarCompagnies, headerText }: TProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const documents = useSelector((state: RootState) => state.article.allSignedDocuments);
    const status = useSelector((state: RootState) => state.article.allSignedDocumentsStatus);
    const [filterCompanies, setFilterCompanies] = useState<IBeneficiariesCompanies[]>([]);
    const [searchInput, setSearchInput] = useState("");
    const [typeFilter, setTypeFilter] = useState<"agreement" | "avenant" | "framework_contract" | undefined>(undefined);

    useEffect(() => {
        dispatch(fetchAllSignedDocuments());
    }, [dispatch]);

    useEffect(() => {
        setFilterCompanies(beneficiarCompagnies);
    }, [beneficiarCompagnies]);

    const filteredDocuments = useMemo(() => {
        const search = searchInput.trim().toLowerCase();
        return documents.filter((doc) => {
            if (filterCompanies.length === 0) return false;
            if (!filterCompanies.some((comp) => comp.id === doc.companyId)) return false;
            if (typeFilter && getCategory(doc) !== typeFilter) return false;
            if (search && !searchHaystack(doc).includes(search)) return false;
            return true;
        });
    }, [documents, filterCompanies, typeFilter, searchInput]);

    const typeFilterOptions = [
        { value: undefined, label: t("ContractValidation.All") },
        { value: "agreement", label: t("BPTitle.DocumentTypeAgreement") },
        { value: "avenant", label: t("BPTitle.DocumentTypeAvenant") },
        { value: "framework_contract", label: t("BPTitle.DocumentTypeFrameworkContract") },
    ];

    const resourceColumnLabel = (() => {
        const types = new Set(beneficiarCompagnies.map((c) => c.publisherType));
        const onlyBook = types.size === 1 && types.has("book");
        const onlyPressAudio = types.size > 0 && [...types].every((p) => p === "press" || p === "audiovisual");
        if (onlyBook) return t("BPTitle.SignedDocumentsTitle");
        if (onlyPressAudio) return t("BPTitle.SignedDocumentsArticle");
        return t("BPTitle.SignedDocumentsResource");
    })();

    const columns: LastElementTableColumns[] = [
        {
            id: "articleName",
            label: resourceColumnLabel,
            align: "left",
            sort: true,
            format: (_value, _id, currentRow) => {
                const doc = currentRow as SignedDocument;
                return doc.articleName || doc.titleName || "-";
            },
        },
        {
            id: "type",
            label: t("BPTitle.SignedDocumentsType"),
            align: "left",
            format: (_value, _id, currentRow) => typeLabel(currentRow as SignedDocument, t),
            sort: true,
        },
        {
            id: "companyId",
            label: t("BeneficiarInfo.Company"),
            align: "left",
            sort: true,
            format: (value) => (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Avatar
                        variant="rounded"
                        sx={{ width: 20, height: 20 }}
                        src={getBPAvatarUrl(value, beneficiarCompagnies)}
                    />
                    {getBPCompanyName(value, beneficiarCompagnies)}
                </div>
            ),
        },
        {
            id: "signedDate",
            label: t("BPTitle.SignedDocumentsDate"),
            align: "left",
            format: (value) =>
                value ? formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }) : "-",
            sort: true,
        },
        {
            id: "download",
            label: "",
            align: "right",
            minWidth: "60px",
            stopPropagation: true,
            format: (_value, _id, currentRow) => (
                <Tooltip title={t("ShowStatements.Download") as string}>
                    <span
                        style={{
                            cursor: "pointer",
                            display: "inline-flex",
                            color: "#000",
                            padding: "4px",
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            dispatch(downloadSignedDocument(currentRow as SignedDocument));
                        }}
                    >
                        <ActionDownload width="18" height="18" />
                    </span>
                </Tooltip>
            ),
        },
    ];

    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewedDoc, setViewedDoc] = useState<SignedDocument | null>(null);

    const handleRowClick = (id: number) => {
        const doc = documents.find((d) => d.id === id);
        if (!doc) return;
        setViewedDoc(doc);
        setViewerOpen(true);
        dispatch(fetchSignedDocumentContent(doc.id));
    };

    const showPicker = beneficiarCompagnies.length > 1;

    return (
        <>
            {(headerText || showPicker) && (
                <div className="bp-settings-documents-filters">
                    {headerText && (
                        <div className="bp-settings-documents-text">
                            <span>{headerText}</span>
                        </div>
                    )}
                    {showPicker && (
                        <div className="picker-company-wrapper">
                            <PickerCompany
                                data={beneficiarCompagnies}
                                selectedData={filterCompanies}
                                setSelectedData={setFilterCompanies}
                            />
                        </div>
                    )}
                </div>
            )}
            <div
                style={{
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                    flexWrap: "wrap",
                    marginBottom: "16px",
                }}
            >
                <div style={{ flex: "1 1 300px", minWidth: "240px" }}>
                    <SmallSearchBar filter={searchInput} onSearch={(e) => setSearchInput(e.target.value)} />
                </div>
                <ControlFilter
                    filterLabel={`${t("ContractValidation.TypeDocumentLabel")} :`}
                    filterOptions={typeFilterOptions}
                    controlValue={typeFilter}
                    onValueChange={(value) => setTypeFilter(value)}
                />
            </div>
            <LastElementTable
                columns={columns}
                data={filteredDocuments}
                onRowClick={handleRowClick}
                onRowClickId="id"
                max={-1}
                dataType="documents"
                isLoading={status === "pending"}
                stickyFooter
            />
            {viewedDoc && (
                <BPSignedDocumentViewer
                    open={viewerOpen}
                    title={viewedDoc.filename?.split(".")[0] ?? `Document_${viewedDoc.id}`}
                    onClose={() => setViewerOpen(false)}
                    onDownload={() => dispatch(downloadSignedDocument(viewedDoc))}
                />
            )}
        </>
    );
};

export default BPSignedDocumentsList;
